import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, ImageOff, ArrowUp, ArrowDown, ImageIcon, Star } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { AddButton, CardControls } from './AdminControls';
import EditDialog from './EditDialog';
import ConfirmDialog from './ConfirmDialog';
import LinkifiedText from './LinkifiedText';

/**
 * Reusable "image + text" tile section that's fully admin-editable.
 * Instantiate with a unique `sectionKey` (e.g. "ministry_cwl_news_events")
 * and it renders a titled grid of tiles that the admin can add / edit /
 * delete / reorder, and upload images for. When there are zero tiles the
 * section is hidden from public visitors (admin still sees the "Add" button).
 *
 * Data lives in the generic `page_tiles` collection under `section_key`.
 */
const TILE_FIELDS = [
  { name: 'date', label: 'Date / Label', placeholder: 'This Sunday' },
  { name: 'title', label: 'Title', required: true },
  {
    name: 'description', label: 'Excerpt', type: 'textarea', required: true, rows: 4,
    help: 'Any URL you paste (e.g. https://example.com) or email address will automatically become a clickable link on the site.',
  },
];

const TileCard = ({
  tile,
  isAdmin,
  uploading,
  onEdit,
  onDelete,
  onFileChosen,
  onRemoveImage,
  onMoveUp,
  onMoveDown,
  onToggleFeature,
  canMoveUp,
  canMoveDown,
}) => {
  const inputRef = useRef(null);
  const src = api.pageTileImageAbs(tile.image_url);

  return (
    <article
      className="relative group bg-white border border-[#e9e0cc] hover:border-[#3a2359] hover:shadow-sm overflow-hidden flex flex-col"
      data-testid={`tile-${tile.id}`}
    >
      {/* Image area */}
      {src ? (
        <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#f2ead6]">
          <img src={src} alt={tile.title} className="w-full h-full object-cover" />
          {isAdmin && (
            <div className="absolute top-2 left-2 flex gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#3a2359] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`tile-change-photo-${tile.id}`}
              >
                {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Camera size={12} /> Change</>}
              </button>
              <button
                type="button"
                onClick={() => onRemoveImage && onRemoveImage(tile)}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#8a2b1a] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`tile-remove-photo-${tile.id}`}
              >
                <ImageOff size={12} /> Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        isAdmin ? (
          <div className="w-full aspect-[16/10] bg-[#f7f0dd] border-b border-[#e9e0cc] flex items-center justify-center">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359] text-xs font-medium disabled:opacity-70"
              data-testid={`tile-add-photo-${tile.id}`}
            >
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Camera size={14} /> Add a photo</>}
            </button>
          </div>
        ) : (
          <div className="w-full aspect-[16/10] bg-[#f2ead6] flex items-center justify-center text-[#a68a5b]">
            <ImageIcon size={22} />
          </div>
        )
      )}

      <div className="p-5 flex-1 flex flex-col">
        {isAdmin && (onEdit || onDelete) && <CardControls onEdit={onEdit} onDelete={onDelete} />}
        {tile.date && (
          <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">{tile.date}</p>
        )}
        <h4 className="font-display text-xl text-[#2a1a45] mb-2 leading-tight">{tile.title}</h4>
        <LinkifiedText
          as="p"
          text={tile.description}
          className="text-sm text-[#5a534a] leading-relaxed whitespace-pre-wrap flex-1"
        />

        {isAdmin && (onMoveUp || onMoveDown) && (
          <div className="mt-4 pt-3 border-t border-[#f2ead6] flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onToggleFeature && onToggleFeature(tile)}
              title={tile.featured_on_home ? 'Remove from home page' : 'Feature on home page'}
              aria-label={tile.featured_on_home ? 'Remove from home page' : 'Feature on home page'}
              data-testid={`tile-toggle-feature-${tile.id}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] tracking-[0.15em] uppercase font-medium ${
                tile.featured_on_home
                  ? 'bg-[#3a2359] border-[#3a2359] text-[#e6d4a8] hover:bg-[#4d2f75]'
                  : 'bg-white border-[#3a2359] text-[#3a2359] hover:bg-[#3a2359] hover:text-white'
              }`}
            >
              <Star size={12} fill={tile.featured_on_home ? 'currentColor' : 'none'} />
              {tile.featured_on_home ? 'Featured' : 'Feature on Home'}
            </button>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Reorder</span>
              <button
                type="button"
                onClick={() => onMoveUp && onMoveUp(tile)}
                disabled={!canMoveUp}
                data-testid={`tile-move-up-${tile.id}`}
                aria-label="Move up"
                className="inline-flex items-center justify-center w-7 h-7 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                onClick={() => onMoveDown && onMoveDown(tile)}
                disabled={!canMoveDown}
                data-testid={`tile-move-down-${tile.id}`}
                aria-label="Move down"
                className="inline-flex items-center justify-center w-7 h-7 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
              >
                <ArrowDown size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {isAdmin && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && onFileChosen) onFileChosen(tile, f);
            if (inputRef.current) inputRef.current.value = '';
          }}
          data-testid={`tile-file-input-${tile.id}`}
        />
      )}
    </article>
  );
};

const TileSection = ({
  sectionKey,
  title,
  eyebrow,
  emptyLabel = 'More coming soon.',
  addLabel = 'Add Tile',
  columns = 3,
  className = '',
}) => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.listPageTiles(sectionKey);
        if (alive) setTiles(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [sectionKey]);

  const handleSave = async (values) => {
    if (editing?.mode === 'new') {
      const { data } = await api.createPageTile(sectionKey, values);
      setTiles(prev => [...prev, data]);
      toast({ title: 'Tile added' });
    } else {
      const { data } = await api.updatePageTile(editing.id, values);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: 'Tile updated' });
    }
  };

  const handleDelete = async () => {
    await api.deletePageTile(deleteTarget.id);
    setTiles(prev => prev.filter(t => t.id !== deleteTarget.id));
    toast({ title: 'Tile deleted' });
    setDeleteTarget(null);
  };

  const handleFile = async (tile, file) => {
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploadingId(tile.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadPageTileImage(tile.id, fd);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemove = async (tile) => {
    setUploadingId(tile.id);
    try {
      const { data } = await api.deletePageTileImage(tile.id);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const move = async (tile, direction) => {
    const idx = tiles.findIndex(t => t.id === tile.id);
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swap < 0 || swap >= tiles.length) return;
    const next = [...tiles];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setTiles(next);
    try {
      const { data } = await api.reorderPageTiles(sectionKey, next.map(t => t.id));
      setTiles(data);
    } catch (e) {
      toast({ title: 'Reorder failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setTiles(tiles);
    }
  };

  const toggleFeature = async (tile) => {
    const nextFeatured = !tile.featured_on_home;
    // Optimistic
    setTiles(prev => prev.map(t => t.id === tile.id ? { ...t, featured_on_home: nextFeatured } : t));
    try {
      const { data } = await api.featurePageTileOnHome(tile.id, nextFeatured);
      setTiles(prev => prev.map(t => t.id === data.id ? data : t));
      toast({ title: nextFeatured ? 'Featured on home page' : 'Removed from home page' });
    } catch (e) {
      // Rollback
      setTiles(prev => prev.map(t => t.id === tile.id ? { ...t, featured_on_home: !nextFeatured } : t));
      toast({ title: 'Update failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  // Hide the section entirely from public visitors when there's nothing to show.
  if (loading) return null;
  if (!isAdmin && tiles.length === 0) return null;

  const colClass = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[columns] || 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className={`mt-14 ${className}`} data-testid={`tile-section-${sectionKey}`}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3">
        <div>
          {eyebrow && <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">{eyebrow}</p>}
          <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45]">{title}</h2>
        </div>
        {isAdmin && <AddButton onClick={() => setEditing({ mode: 'new' })} label={addLabel} />}
      </div>

      {tiles.length === 0 ? (
        <p className="text-center text-[#78716c] py-6 text-sm">
          {isAdmin ? 'No tiles yet — add your first one above.' : emptyLabel}
        </p>
      ) : (
        <div className={`grid grid-cols-1 ${colClass} gap-5`}>
          {tiles.map((t, i) => (
            <TileCard
              key={t.id}
              tile={t}
              isAdmin={isAdmin}
              uploading={uploadingId === t.id}
              onEdit={() => setEditing(t)}
              onDelete={() => setDeleteTarget(t)}
              onFileChosen={handleFile}
              onRemoveImage={handleRemove}
              onMoveUp={(x) => move(x, 'up')}
              onMoveDown={(x) => move(x, 'down')}
              onToggleFeature={toggleFeature}
              canMoveUp={i > 0}
              canMoveDown={i < tiles.length - 1}
            />
          ))}
        </div>
      )}

      <EditDialog
        open={!!editing}
        title={editing?.mode === 'new' ? 'Add News or Event' : 'Edit'}
        fields={TILE_FIELDS}
        initial={editing?.mode === 'new' ? { date: '', title: '', description: '' } : (editing || {})}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this tile?"
        message={deleteTarget ? `“${deleteTarget.title}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
};

export default TileSection;
export { TileCard };
